'use client';

import { c as _c } from "react/compiler-runtime";
import { useForm } from '@tanstack/react-form';
import { useCreateProject, useGetProjects } from '@/api/generated/projects/projects';
import { createProjectOnboardingInitialValues, createProjectOnboardingSchema } from '@/domains/onboarding/components/create-project-onboarding/create-project-onboarding.schema';
import { ProjectProductSelector } from '@/domains/dashboard/components/project-product-selector';
import { getFormFieldErrorMessage } from '@/domains/reports/schemas/report-identity.schema';
import { useLogout } from '@/hooks/use-logout';
import { useProjectsContext } from '@/providers/projects-provider';
import { useQueryClient } from '@tanstack/react-query';
import { QUERY_KEY_ORGANIZATIONS } from '@/constants/query-keys';
import { Button } from '@/comps/primitives/button';
import { Input } from '@/comps/primitives/input';
import { Loader2 } from 'lucide-react';
export const CreateProjectOnboarding = () => {
  const $ = _c(22);
  const {
    logout
  } = useLogout();
  const {
    refetch: refetchProjects
  } = useGetProjects();
  const {
    organizationId
  } = useProjectsContext();
  const client = useQueryClient();
  let t0;
  if ($[0] !== client || $[1] !== refetchProjects) {
    t0 = async () => {
      await client.invalidateQueries({
        queryKey: [QUERY_KEY_ORGANIZATIONS]
      });
      await refetchProjects();
    };
    $[0] = client;
    $[1] = refetchProjects;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const navigateToHome = t0;
  const {
    mutateAsync
  } = useCreateProject();
  let t1;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      onSubmit: createProjectOnboardingSchema
    };
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== mutateAsync || $[5] !== navigateToHome || $[6] !== organizationId) {
    t2 = {
      defaultValues: createProjectOnboardingInitialValues,
      validators: t1,
      onSubmit: async t3 => {
        const {
          value,
          formApi
        } = t3;
        const resp = await mutateAsync({
          data: {
            name: value.projectName.trim(),
            organization_id: organizationId ?? "",
            product: value.product
          }
        });
        formApi.reset();
        if (!resp.project) {
          return;
        }
        await navigateToHome();
      }
    };
    $[4] = mutateAsync;
    $[5] = navigateToHome;
    $[6] = organizationId;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const form = useForm(t2);
  let t3;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <div className="flex flex-col gap-1.5"><h1 className="text-2xl font-medium">Create your first project</h1><p className="text-sm text-muted-foreground">Give a name to the first LLM project you want to track analytics for.</p></div>;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  let t5;
  if ($[9] !== form.Field) {
    t4 = <form.Field name="projectName">{_temp}</form.Field>;
    t5 = <form.Field name="product">{_temp2}</form.Field>;
    $[9] = form.Field;
    $[10] = t4;
    $[11] = t5;
  } else {
    t4 = $[10];
    t5 = $[11];
  }
  let t6;
  if ($[12] !== form || $[13] !== logout) {
    t6 = isSubmitting => <div className="mt-2 flex justify-end gap-2"><Button variant="ghost" onClick={() => logout()}>Back</Button><Button disabled={isSubmitting} onClick={() => {
        form.handleSubmit();
      }}>{isSubmitting ? <Loader2 className="size-3.5 animate-spin" /> : null}Continue</Button></div>;
    $[12] = form;
    $[13] = logout;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== form.Subscribe || $[16] !== t6) {
    t7 = <form.Subscribe selector={_temp3}>{t6}</form.Subscribe>;
    $[15] = form.Subscribe;
    $[16] = t6;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  let t8;
  if ($[18] !== t4 || $[19] !== t5 || $[20] !== t7) {
    t8 = <div className="fadeIn flex h-screen items-center justify-center"><div className="flex w-full max-w-125 flex-col gap-2.5">{t3}{t4}{t5}{t7}</div></div>;
    $[18] = t4;
    $[19] = t5;
    $[20] = t7;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  return t8;
};
function _temp(field) {
  return <div><Input placeholder="My LLM in production" autoFocus={true} id="projectName" name="projectName" value={field.state.value} onChange={e => field.handleChange(e.target.value)} invalid={field.state.meta.errors.length > 0} />{field.state.meta.errors.length > 0 ? <p className="mt-1 text-xs text-destructive">{getFormFieldErrorMessage(field.state.meta.errors)}</p> : null}</div>;
}
function _temp2(field_0) {
  return <ProjectProductSelector value={field_0.state.value} onChange={field_0.handleChange} />;
}
function _temp3(state) {
  return state.isSubmitting;
}